<template>
  <div class="revenue-analysis-page">
    <template v-if="isAuthorized">
      <div class="page-header">
        <PageHeaderPortal>
          <div class="page-header-left">
            <div class="page-icon">
              <i class="fa-duotone fa-chart-mixed-up-circle-dollar"></i>
            </div>

            <div>
              <h1 class="page-title">Revenue Analysis</h1>
              <p class="page-subtitle">Revenue alerts, the figures behind them, and who they reached</p>
            </div>
          </div>
        </PageHeaderPortal>

        <div class="header-actions">
          <select v-model.number="days" class="filter-select days-select" @change="loadData">
            <option :value="7">Last 7 Days</option>
            <option :value="14">Last 14 Days</option>
            <option :value="30">Last 30 Days</option>
            <option :value="60">Last 60 Days</option>
            <option :value="90">Last 90 Days</option>
            <option :value="180">Last 180 Days</option>
            <option :value="365">Last 365 Days</option>
          </select>

          <button class="export-btn" :disabled="loading || !visibleRows.length" @click="exportCsv">
            Export CSV
          </button>

          <button class="refresh-btn" :disabled="loading" @click="loadData">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="page-error">{{ errorMessage }}</div>

      <!-- The test redirect is the one thing that makes "emailed to" mean something other than
           what it says, so it is stated before any of the routing figures rather than inside
           them. -->
      <div v-if="redirectSummary.count" class="redirect-banner">
        <i class="fa-duotone fa-envelope-circle-check"></i>
        <div>
          <strong>{{ redirectSummary.count }}</strong> of these alerts were diverted by the test
          redirect to <strong>{{ redirectSummary.mailboxes.join(', ') }}</strong>.
          The recipients below are who each alert was <em>addressed</em> to — they did not receive
          those copies.
        </div>
      </div>

      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-head">
            <i class="fa-duotone fa-triangle-exclamation stat-icon"></i>
            <span class="stat-label">Alerts</span>
          </div>
          <div class="stat-value">{{ summary.total }}</div>
          <div class="stat-foot">{{ summary.loans }} loans · {{ summary.branches }} branches</div>
        </div>

        <div class="stat-card">
          <div class="stat-head">
            <i class="fa-duotone fa-hourglass-half stat-icon"></i>
            <span class="stat-label">Still open</span>
          </div>
          <div class="stat-value">{{ summary.open }}</div>
          <div class="stat-foot">
            {{ summary.oldestOpenSeconds === null ? 'nothing waiting' : 'oldest ' + formatDuration(summary.oldestOpenSeconds) }}
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-head">
            <i class="fa-duotone fa-circle-check stat-icon"></i>
            <span class="stat-label">Worked</span>
          </div>
          <div class="stat-value">{{ summary.done }}</div>
          <div class="stat-foot">{{ percentText(summary.done, summary.total) }} of the window</div>
        </div>

        <div class="stat-card">
          <div class="stat-head">
            <i class="fa-duotone fa-stopwatch stat-icon"></i>
            <span class="stat-label">Median turnaround</span>
          </div>
          <div class="stat-value">{{ summary.medianTurnaround === null ? '—' : formatDuration(summary.medianTurnaround) }}</div>
          <!-- The timer that flags an item differs by type (24h for a branch deposit alert, on
               arrival for the wholesale ones), so this counts flags rather than naming a
               duration the page would sometimes get wrong. -->
          <div class="stat-foot">
            {{ summary.breached }} flagged high priority
          </div>
        </div>

        <div class="stat-card" :class="{ 'stat-warn': routing.reachedNobody > 0 }">
          <div class="stat-head">
            <i class="fa-duotone fa-user-slash stat-icon"></i>
            <span class="stat-label">Reached nobody</span>
          </div>
          <div class="stat-value">{{ routing.reachedNobody }}</div>
          <div class="stat-foot">no recipient resolved for the loan</div>
        </div>

        <div class="stat-card">
          <div class="stat-head">
            <i class="fa-duotone fa-sack-dollar stat-icon"></i>
            <span class="stat-label">Branch margin on file</span>
          </div>
          <div class="stat-value">{{ formatMoneyShort(summary.branchMarginTotal) }}</div>
          <div class="stat-foot">
            {{ summary.withSummary }} of {{ summary.total }} alerts carry a summary
          </div>
        </div>
      </div>

      <div class="panel-row">
        <section class="panel">
          <div class="panel-header">
            <div class="panel-title-wrap">
              <i class="fa-duotone fa-layer-group panel-icon"></i>
              <span class="panel-title">What is alerting</span>
            </div>
          </div>

          <div v-if="!summary.total" class="panel-empty">No alerts in this window.</div>

          <div v-for="bucket in typeMix" :key="bucket.key" class="mix-row" :class="{ 'mix-row-zero': !bucket.count }">
            <div class="mix-head">
              <span class="type-pill" :class="bucket.pillClass">{{ bucket.label }}</span>
              <span class="mix-count">{{ bucket.count }}</span>
            </div>
            <div class="mix-bar">
              <div class="mix-bar-fill" :class="bucket.pillClass" :style="{ width: bucket.share + '%' }"></div>
            </div>
            <div class="mix-foot">
              {{ percentText(bucket.count, summary.total) }}
              <span v-if="bucket.avgBps !== null"> · avg {{ formatBps(bucket.avgBps) }}</span>
              <span v-if="bucket.open"> · {{ bucket.open }} open</span>
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel-header">
            <div class="panel-title-wrap">
              <i class="fa-duotone fa-chart-column panel-icon"></i>
              <span class="panel-title">When they arrive</span>
            </div>
            <span class="panel-note">{{ trend.unitLabel }}</span>
          </div>

          <div v-if="!trend.buckets.length" class="panel-empty">No alerts in this window.</div>

          <div v-else class="trend-chart">
            <div v-for="bucket in trend.buckets" :key="bucket.key" class="trend-col" :title="bucket.title">
              <div class="trend-bar-wrap">
                <div class="trend-bar" :style="{ height: bucket.height + '%' }"></div>
              </div>
              <div class="trend-count">{{ bucket.count || '' }}</div>
              <div class="trend-label">{{ bucket.label }}</div>
            </div>
          </div>
        </section>
      </div>

      <section class="panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <i class="fa-duotone fa-paper-plane panel-icon"></i>
            <span class="panel-title">Who the alerts were emailed to</span>
          </div>
          <span class="panel-note">
            from the routing recorded on each work item
          </span>
        </div>

        <!-- Without this, the "no routing recorded" tile is a bare number with no way to tell
             whether it means a failure or a limit of the record. It is the latter: recipients are
             recorded ON the work item, and only since 2026-09-11. -->
        <div v-if="routing.noRouting" class="panel-hint">
          <i class="fa-duotone fa-circle-info"></i>
          <div>
            <strong>{{ routing.noRouting }}</strong> of these alerts carry no routing note, so who
            they reached cannot be answered. Recipients are recorded on the work item itself, and an
            alert has none if it arrived before that recording began, or if its alert email was
            switched off in ProcessControl at the time.<span v-if="routing.knownSince">
            Recipients are known for alerts from {{ formatDate(routing.knownSince) }} onward.</span>
            Every other figure on this page still counts them.
          </div>
        </div>

        <div class="routing-stats">
          <div class="routing-stat">
            <div class="routing-stat-value">{{ routing.withRecipients }}</div>
            <div class="routing-stat-label">alerts emailed</div>
          </div>
          <div class="routing-stat">
            <div class="routing-stat-value">{{ recipientRows.length }}</div>
            <div class="routing-stat-label">distinct recipients</div>
          </div>
          <div class="routing-stat" :class="{ 'routing-stat-warn': routing.reachedNobody > 0 }">
            <div class="routing-stat-value">{{ routing.reachedNobody }}</div>
            <div class="routing-stat-label">emailed nobody</div>
          </div>
          <div class="routing-stat" :class="{ 'routing-stat-warn': routing.noRouting > 0 }">
            <div class="routing-stat-value">{{ routing.noRouting }}</div>
            <div class="routing-stat-label">no routing recorded</div>
          </div>
          <div class="routing-stat">
            <div class="routing-stat-value">{{ refusedRows.length }}</div>
            <div class="routing-stat-label">addresses refused</div>
          </div>
        </div>

        <div class="routing-tables">
          <div class="routing-table-wrap">
            <div class="sub-title">Recipients</div>

            <div class="scroll-box">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th class="num">Alerts</th>
                    <th class="num">To</th>
                    <th class="num">CC</th>
                    <th class="num">Branches</th>
                    <th>Last alert</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!recipientRows.length">
                    <td colspan="6" class="empty-cell">No routing has been recorded for these alerts.</td>
                  </tr>
                  <tr v-for="r in recipientRows" :key="r.key">
                    <td class="mono">{{ r.email }}</td>
                    <td class="num strong">{{ r.total }}</td>
                    <td class="num">{{ r.to }}</td>
                    <td class="num">{{ r.cc }}</td>
                    <td class="num">{{ r.branches }}</td>
                    <td>{{ formatDate(r.last) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="routing-table-wrap">
            <div class="sub-title">Refused by the recipient rules</div>

            <div class="scroll-box">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th class="num">Times</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!refusedRows.length">
                    <td colspan="3" class="empty-cell">Nothing was turned away.</td>
                  </tr>
                  <tr v-for="r in refusedRows" :key="r.key">
                    <td class="mono">{{ r.email }}</td>
                    <td class="num strong">{{ r.total }}</td>
                    <td class="reason-cell">{{ r.reasons.join(' · ') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="noRecipientRows.length" class="nobody-block">
              <div class="sub-title">Emailed nobody</div>
              <ul class="nobody-list">
                <li v-for="row in noRecipientRows.slice(0, 8)" :key="row.id">
                  <span class="mono">{{ row.loan_number || '—' }}</span>
                  <span class="nobody-meta">
                    {{ row.loan_officer_name || 'no LO on the item' }} ·
                    {{ row.branch || row.orgid || 'no branch' }} ·
                    {{ formatDate(row.arrived_date) }}
                  </span>
                </li>
              </ul>
              <div v-if="noRecipientRows.length > 8" class="nobody-more">
                and {{ noRecipientRows.length - 8 }} more — filter the table below to Emailed nobody.
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="panel-row">
        <section class="panel">
          <div class="panel-header">
            <div class="panel-title-wrap">
              <i class="fa-duotone fa-building panel-icon"></i>
              <span class="panel-title">Branches</span>
            </div>
            <span class="panel-note">top {{ Math.min(branchRows.length, 10) }} by alerts</span>
          </div>

          <table class="data-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th class="num">Alerts</th>
                <th class="num">Avg bps</th>
                <th class="num">Branch margin</th>
                <th class="num">Concessions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!branchRows.length">
                <td colspan="5" class="empty-cell">No alerts in this window.</td>
              </tr>
              <tr v-for="b in branchRows.slice(0, 10)" :key="b.key">
                <td class="strong">{{ b.label }}</td>
                <td class="num strong">{{ b.count }}</td>
                <td class="num">{{ b.avgBps === null ? '—' : formatBps(b.avgBps) }}</td>
                <td class="num">{{ formatMoneyShort(b.branchMargin) }}</td>
                <td class="num">{{ formatMoneyShort(b.concessions) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="panel">
          <div class="panel-header">
            <div class="panel-title-wrap">
              <i class="fa-duotone fa-user-tie panel-icon"></i>
              <span class="panel-title">Loan officers</span>
            </div>
            <span class="panel-note">top {{ Math.min(officerRows.length, 10) }} by alerts</span>
          </div>

          <table class="data-table">
            <thead>
              <tr>
                <th>Loan officer</th>
                <th class="num">Alerts</th>
                <th class="num">Loans</th>
                <th class="num">Avg bps</th>
                <th class="num">LO comp</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!officerRows.length">
                <td colspan="5" class="empty-cell">No alerts in this window.</td>
              </tr>
              <tr v-for="o in officerRows.slice(0, 10)" :key="o.key">
                <td class="strong">{{ o.label }}</td>
                <td class="num strong">{{ o.count }}</td>
                <td class="num">{{ o.loans }}</td>
                <td class="num">{{ o.avgBps === null ? '—' : formatBps(o.avgBps) }}</td>
                <td class="num">{{ formatMoneyShort(o.loComp) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      <section class="panel table-panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <i class="fa-duotone fa-table-list panel-icon"></i>
            <span class="panel-title">Every alert</span>
          </div>
          <span class="panel-count">{{ visibleRows.length }} of {{ rows.length }}</span>
        </div>

        <div class="toolbar">
          <input v-model.trim="search"
                 class="search-input"
                 type="text"
                 placeholder="Search loan, borrower, LO, branch or recipient" />

          <select v-model="typeFilter" class="filter-select">
            <option value="">All types</option>
            <option v-for="opt in typeOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
          </select>

          <select v-model="statusFilter" class="filter-select">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="not_started">Not started</option>
            <option value="in_progress">In progress</option>
            <option value="done">Done</option>
          </select>

          <select v-model="branchFilter" class="filter-select">
            <option value="">All branches</option>
            <option v-for="opt in branchOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>

          <select v-model="routingFilter" class="filter-select">
            <option value="">Any routing</option>
            <option value="emailed">Emailed someone</option>
            <option value="nobody">Emailed nobody</option>
            <option value="none">No routing recorded</option>
            <option value="redirected">Diverted by the test redirect</option>
            <option value="refused">Something was refused</option>
          </select>
        </div>

        <div class="table-wrap">
          <table class="data-table alerts-table">
            <thead>
              <tr>
                <th class="expand-col"></th>
                <th @click="sort('arrived_date')">Arrived {{ sortMark('arrived_date') }}</th>
                <th @click="sort('request_type')">Type {{ sortMark('request_type') }}</th>
                <th @click="sort('loan_number')">Loan {{ sortMark('loan_number') }}</th>
                <th>Borrower</th>
                <th @click="sort('loan_officer_name')">Loan officer {{ sortMark('loan_officer_name') }}</th>
                <th @click="sort('branchLabel')">Branch {{ sortMark('branchLabel') }}</th>
                <th class="num" @click="sort('deposit_bps')">bps {{ sortMark('deposit_bps') }}</th>
                <th class="num" @click="sort('forecast_deposit')">Forecast {{ sortMark('forecast_deposit') }}</th>
                <th class="num" @click="sort('branch_margin')">Branch margin {{ sortMark('branch_margin') }}</th>
                <th @click="sort('recipientCount')">Emailed to {{ sortMark('recipientCount') }}</th>
                <th @click="sort('status')">Status {{ sortMark('status') }}</th>
                <th class="num" @click="sort('duration_seconds')">Turnaround {{ sortMark('duration_seconds') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="loading">
                <td colspan="13" class="table-state">Loading revenue alerts...</td>
              </tr>

              <tr v-else-if="!visibleRows.length">
                <td colspan="13" class="table-state">No alerts match these filters.</td>
              </tr>

              <template v-for="row in pagedRows" :key="row.id">
                <tr class="alert-row" :class="{ 'is-open': expandedId === row.id }" @click="toggleExpand(row)">
                  <td class="expand-col">
                    <i class="fa-solid" :class="expandedId === row.id ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                  </td>
                  <td>{{ formatDate(row.arrived_date) }}</td>
                  <td>
                    <span class="type-pill" :class="typePillClass(row.request_type)">{{ typeLabel(row.request_type) }}</span>
                  </td>
                  <td class="mono strong">{{ row.loan_number || '—' }}</td>
                  <td>{{ borrowerName(row) }}</td>
                  <td>{{ row.loan_officer_name || '—' }}</td>
                  <td>{{ row.branchLabel || '—' }}</td>
                  <td class="num">{{ row.deposit_bps === null ? '—' : formatBps(row.deposit_bps) }}</td>
                  <td class="num" :class="{ negative: (row.forecast_deposit || 0) < 0 }">
                    {{ row.forecast_deposit === null ? '—' : formatMoney(row.forecast_deposit) }}
                  </td>
                  <td class="num">{{ row.branch_margin === null ? '—' : formatMoney(row.branch_margin) }}</td>
                  <td>
                    <span v-if="row.recipientCount" class="routing-chip" :title="allRecipients(row).join(', ')">
                      {{ row.emailed_to[0] || row.emailed_cc[0] }}
                      <span v-if="row.recipientCount > 1" class="routing-more">+{{ row.recipientCount - 1 }}</span>
                    </span>
                    <span v-else-if="row.has_routing" class="routing-chip chip-warn">nobody</span>
                    <span v-else class="routing-chip chip-muted">not recorded</span>
                  </td>
                  <td>
                    <span class="status-pill" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
                  </td>
                  <td class="num">{{ formatDuration(row.duration_seconds) }}</td>
                </tr>

                <tr v-if="expandedId === row.id" :key="row.id + '-detail'" class="detail-row">
                  <td colspan="13">
                    <div class="detail-grid">
                      <div class="detail-block">
                        <div class="sub-title">Deposit summary</div>
                        <div v-if="!row.has_summary" class="detail-empty">
                          No summary note — the Encompass read failed when this alert was raised.
                        </div>
                        <dl v-else class="detail-list">
                          <div><dt>Loan product</dt><dd>{{ row.loan_product || '—' }}</dd></div>
                          <div><dt>Loan amount</dt><dd>{{ row.loan_amount === null ? '—' : formatMoney(row.loan_amount) }}</dd></div>
                          <div><dt>Forecast deposit</dt><dd>{{ row.forecast_deposit === null ? '—' : formatMoney(row.forecast_deposit) }}</dd></div>
                          <div><dt>Branch margin</dt><dd>{{ row.branch_margin === null ? '—' : formatMoney(row.branch_margin) }}</dd></div>
                          <div><dt>Branch concession</dt><dd>{{ row.branch_concession === null ? '—' : formatMoney(row.branch_concession) }}</dd></div>
                          <div><dt>Corp concession</dt><dd>{{ row.corp_concession === null ? '—' : formatMoney(row.corp_concession) }}</dd></div>
                          <div><dt>Lender credit</dt><dd>{{ row.lender_credit === null ? '—' : formatMoney(row.lender_credit) }}</dd></div>
                          <div><dt>LO comp</dt><dd>{{ row.lo_comp === null ? '—' : formatMoney(row.lo_comp) }}</dd></div>
                          <div><dt>Lead type</dt><dd>{{ row.lead_type || '—' }}</dd></div>
                          <div><dt>Branch</dt><dd>{{ row.branch || row.orgid || '—' }}</dd></div>
                        </dl>
                      </div>

                      <div class="detail-block">
                        <div class="sub-title">Email routing</div>
                        <div v-if="!row.has_routing" class="detail-empty">
                          No routing recorded. Either the alert email was switched off in
                          ProcessControl when this arrived, or the alert predates routing being
                          recorded on the item.
                        </div>
                        <template v-else>
                          <div class="routing-line">
                            <span class="routing-label">To</span>
                            <span v-if="row.emailed_to.length" class="routing-addresses">
                              <span v-for="a in row.emailed_to" :key="'to-' + a" class="address-chip">{{ a }}</span>
                            </span>
                            <span v-else class="routing-none">nobody — no recipient resolved for this loan</span>
                          </div>

                          <div class="routing-line">
                            <span class="routing-label">CC</span>
                            <span v-if="row.emailed_cc.length" class="routing-addresses">
                              <span v-for="a in row.emailed_cc" :key="'cc-' + a" class="address-chip">{{ a }}</span>
                            </span>
                            <span v-else class="routing-none">nobody</span>
                          </div>

                          <div v-if="row.redirected_to" class="routing-line">
                            <span class="routing-label">Delivered</span>
                            <span class="routing-addresses">
                              <span class="address-chip chip-redirect">{{ row.redirected_to }}</span>
                              <span class="routing-note-text">test redirect — the lists above did not receive it</span>
                            </span>
                          </div>

                          <div v-if="row.refused_not_approver.length" class="routing-line">
                            <span class="routing-label">Refused</span>
                            <span class="routing-addresses">
                              <span v-for="a in row.refused_not_approver" :key="'rf-' + a" class="address-chip chip-refused">{{ a }}</span>
                              <span class="routing-note-text">not a listed recipient for this loan, not a standing reviewer</span>
                            </span>
                          </div>

                          <div v-if="row.refused_loan_officer.length" class="routing-line">
                            <span class="routing-label">Refused</span>
                            <span class="routing-addresses">
                              <span v-for="a in row.refused_loan_officer" :key="'rlo-' + a" class="address-chip chip-refused">{{ a }}</span>
                              <span class="routing-note-text">this loan's own officer</span>
                            </span>
                          </div>

                          <div v-if="row.configured_ccs" class="routing-line">
                            <span class="routing-label">CC setting</span>
                            <span class="routing-note-text mono">{{ row.configured_ccs }}</span>
                          </div>
                        </template>
                      </div>

                      <div class="detail-block">
                        <div class="sub-title">On the desk</div>
                        <dl class="detail-list">
                          <div><dt>Trigger</dt><dd>{{ row.request_description || '—' }}</dd></div>
                          <div><dt>Arrived</dt><dd>{{ formatDateTime(row.arrived_date) }}</dd></div>
                          <div><dt>Picked up</dt><dd>{{ row.assigned_utc ? formatDateTime(row.assigned_utc) : 'not yet' }}</dd></div>
                          <div><dt>Waited</dt><dd>{{ formatDuration(row.wait_seconds) }}</dd></div>
                          <div><dt>Completed</dt><dd>{{ row.completed_utc ? formatDateTime(row.completed_utc) : 'open' }}</dd></div>
                          <div><dt>Worked by</dt><dd>{{ row.completed_by_name || row.started_by_name || row.assigned_to_name || '—' }}</dd></div>
                          <div><dt>Turnaround</dt><dd>{{ formatDuration(row.duration_seconds) }}</dd></div>
                          <div><dt>Notes</dt><dd>{{ row.note_count }} ({{ row.external_note_count }} shared with the LO)</dd></div>
                        </dl>

                        <div class="detail-links">
                          <a class="polly-btn"
                             :class="{ 'is-disabled': !row.loan_id }"
                             :href="pollyUrl(row)"
                             target="_blank"
                             rel="noopener noreferrer"
                             @click.stop="openLoanLink(pollyUrl(row), $event)">
                            Open Polly
                          </a>
                          <a class="encompass-btn"
                             :class="{ 'is-disabled': !row.loan_id }"
                             :href="encompassUrl(row)"
                             target="_blank"
                             rel="noopener noreferrer"
                             @click.stop="openLoanLink(encompassUrl(row), $event)">
                            Open Encompass
                          </a>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="pagination-bar">
          <div class="pagination-info">
            Showing {{ pagedRows.length }} of {{ visibleRows.length }}
            <span v-if="loadedAt"> · loaded {{ formatDateTime(loadedAt) }}</span>
          </div>
          <div class="pagination-actions">
            <button class="page-btn" :disabled="page <= 1" @click="page -= 1">Prev</button>
            <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
            <button class="page-btn" :disabled="page >= totalPages" @click="page += 1">Next</button>
          </div>
        </div>
      </section>
    </template>

    <div v-else-if="accessLoading" class="state-card">Checking your access...</div>

    <div v-else class="not-authorized-card">
      <div class="not-authorized-icon">
        <i class="fa-duotone fa-lock"></i>
      </div>
      <div class="not-authorized-title">Not authorized</div>
      <div class="not-authorized-text">
        Revenue Analysis is limited to concession administrators — it reports margin, comp and
        alert recipients across every division.
      </div>
      <div v-if="accessError" class="not-authorized-text">{{ accessError }}</div>
    </div>
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import api from '@/api/api.service'
  import auth from "@/authConfig.js"
  import { openLoanInWindow } from '@/helpers/openLoanWindow.js'

  // The three request types the revenue alert pipeline produces, in the order the desk reads
  // them. Deliberately a list of the types this page KNOWS: an unrecognised type still renders
  // (the API decides what a revenue alert is, not this file) — it just falls back to its raw
  // value rather than being dropped. See the lock desk queue for the same reasoning.
  const TYPE_META = {
    revenue_alert: { label: 'Branch Deposit', pill: 'type-deposit' },
    wholesale_lpc_missing: { label: 'Wholesale LPC Missing', pill: 'type-wholesale' },
    wholesale_lpc_high: { label: 'Wholesale LPC High', pill: 'type-wholesale' }
  }

  const TERMINAL_STATUSES = ['done', 'completed', 'cancelled']

  export default {
    name: 'RevenueAnalysis',
    components: { PageHeaderPortal },
    data() {
      return {
        // Access is resolved by the API, not read off localStorage — see isAuthorized.
        accessLoading: true,
        accessError: '',
        isConcessionsAdmin: false,
        loading: false,
        errorMessage: '',
        rows: [],
        days: 90,
        loadedAt: null,
        search: '',
        typeFilter: '',
        statusFilter: '',
        branchFilter: '',
        routingFilter: '',
        sortBy: 'arrived_date',
        sortDir: 'desc',
        page: 1,
        pageSize: 50,
        expandedId: '',
        // Polly's org id, as the queue and the extensions dashboard both use it for loan links.
        pollyOrgId: 698
      }
    },
    computed: {
      // ConcessionsAdmin, resolved by the API off the acting azure id (GetConcessionUserContext,
      // the same call the concessions page gates its admin views on) rather than off the roles in
      // localStorage: this role is not in that list, and the endpoint enforces it server-side
      // anyway. The page only mirrors the server's answer so it can say so rather than showing an
      // empty dashboard.
      //
      // Deliberately narrower than the swiftLocks group it sits in. The group rides
      // canViewRepricedLocks and children are not gate-filtered, so a lock desk user sees the
      // link and lands on the message below — the same arrangement Concessions already has.
      isAuthorized() {
        return this.isConcessionsAdmin
      },
      typeOptions() {
        const seen = new Map()
        for (const row of this.rows) {
          const key = this.typeKey(row.request_type)
          if (!seen.has(key)) seen.set(key, { key, label: this.typeLabel(row.request_type) })
        }
        return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label))
      },
      branchOptions() {
        return [...new Set(this.rows.map(r => r.branchLabel).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      },
      visibleRows() {
        const q = this.search.toLowerCase()

        return this.rows.filter(row => {
          if (this.typeFilter && this.typeKey(row.request_type) !== this.typeFilter) return false

          if (this.statusFilter) {
            const status = this.statusKey(row.status)
            if (this.statusFilter === 'open') {
              if (TERMINAL_STATUSES.includes(status)) return false
            } else if (this.statusFilter === 'done') {
              if (!TERMINAL_STATUSES.includes(status)) return false
            } else if (status !== this.statusFilter) {
              return false
            }
          }

          if (this.branchFilter && row.branchLabel !== this.branchFilter) return false

          if (this.routingFilter === 'emailed' && !row.recipientCount) return false
          if (this.routingFilter === 'nobody' && !(row.has_routing && !row.recipientCount)) return false
          if (this.routingFilter === 'none' && row.has_routing) return false
          if (this.routingFilter === 'redirected' && !row.redirected_to) return false
          if (this.routingFilter === 'refused'
              && !row.refused_not_approver.length
              && !row.refused_loan_officer.length) return false

          if (!q) return true

          return row.searchText.includes(q)
        }).sort((a, b) => this.compareRows(a, b))
      },
      summary() {
        const rows = this.visibleRows
        const open = rows.filter(r => !TERMINAL_STATUSES.includes(this.statusKey(r.status)))
        const done = rows.filter(r => TERMINAL_STATUSES.includes(this.statusKey(r.status)))

        const turnarounds = done
          .map(r => r.duration_seconds)
          .filter(x => typeof x === 'number' && x >= 0)

        const openAges = open
          .map(r => this.ageSeconds(r.arrived_date))
          .filter(x => x !== null)

        return {
          total: rows.length,
          open: open.length,
          done: done.length,
          loans: new Set(rows.map(r => r.loan_number || r.loan_id).filter(Boolean)).size,
          branches: new Set(rows.map(r => r.branchLabel).filter(Boolean)).size,
          medianTurnaround: this.median(turnarounds),
          oldestOpenSeconds: openAges.length ? Math.max(...openAges) : null,
          breached: rows.filter(r => r.is_high_priority === true || r.is_high_priority === 1).length,
          withSummary: rows.filter(r => r.has_summary).length,
          branchMarginTotal: this.sum(rows.map(r => r.branch_margin))
        }
      },
      typeMix() {
        const groups = new Map()

        // Seeded with every type the pipeline can raise, so a check that fired nothing in this
        // window reads as a zero rather than vanishing — "no wholesale LPC alerts" and "wholesale
        // LPC is not on this page" look identical otherwise, and only one of them is true.
        for (const key of Object.keys(TYPE_META)) {
          groups.set(key, {
            key,
            label: TYPE_META[key].label,
            pillClass: TYPE_META[key].pill,
            count: 0,
            open: 0,
            bps: []
          })
        }

        for (const row of this.visibleRows) {
          const key = this.typeKey(row.request_type)
          if (!groups.has(key)) {
            groups.set(key, {
              key,
              label: this.typeLabel(row.request_type),
              pillClass: this.typePillClass(row.request_type),
              count: 0,
              open: 0,
              bps: []
            })
          }

          const bucket = groups.get(key)
          bucket.count += 1
          if (!TERMINAL_STATUSES.includes(this.statusKey(row.status))) bucket.open += 1
          if (row.deposit_bps !== null) bucket.bps.push(row.deposit_bps)
        }

        const max = Math.max(1, ...[...groups.values()].map(g => g.count))

        return [...groups.values()]
          .map(g => ({
            ...g,
            avgBps: g.bps.length ? this.sum(g.bps) / g.bps.length : null,
            share: Math.round((g.count / max) * 100)
          }))
          .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      },
      // Daily buckets for a short window, weekly beyond it — 90 days of daily bars is unreadable
      // and 7 days of weekly ones says nothing.
      trend() {
        const rows = this.visibleRows
        if (!rows.length) return { buckets: [], unitLabel: '' }

        const daily = this.days <= 21
        const counts = new Map()

        for (const row of rows) {
          const date = new Date(row.arrived_date)
          if (isNaN(date.getTime())) continue

          const bucket = daily ? this.startOfDay(date) : this.startOfWeek(date)
          const key = bucket.toISOString().slice(0, 10)
          counts.set(key, (counts.get(key) || 0) + 1)
        }

        const keys = [...counts.keys()].sort()
        const max = Math.max(1, ...counts.values())

        return {
          unitLabel: daily ? 'by day' : 'week beginning',
          buckets: keys.map(key => {
            const count = counts.get(key)
            const date = new Date(key + 'T00:00:00')
            return {
              key,
              count,
              height: Math.round((count / max) * 100),
              label: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
              title: `${count} alert${count === 1 ? '' : 's'} ${daily ? 'on' : 'in the week of'} ${date.toLocaleDateString('en-US')}`
            }
          })
        }
      },
      routing() {
        const rows = this.visibleRows
        const routed = rows.filter(r => r.has_routing)

        // The earliest alert in this window that recorded its routing. Read off the data rather
        // than hardcoded, because it answers the question the bare "no routing recorded" count
        // raises — "then which ones DO I know about" — and stays true as that history ages out.
        const routedTimes = routed
          .map(r => new Date(r.arrived_date).getTime())
          .filter(t => !isNaN(t))

        return {
          withRecipients: rows.filter(r => r.recipientCount > 0).length,
          // Routing WAS resolved and came back empty — nothing on the loan's roster and no branch
          // manager email. Kept apart from "no routing recorded", which is a different failure.
          reachedNobody: rows.filter(r => r.has_routing && !r.recipientCount).length,
          noRouting: rows.length - routed.length,
          knownSince: routedTimes.length ? new Date(Math.min(...routedTimes)) : null
        }
      },
      redirectSummary() {
        const diverted = this.visibleRows.filter(r => r.redirected_to)

        return {
          count: diverted.length,
          mailboxes: [...new Set(diverted.flatMap(r => this.splitAddresses(r.redirected_to)))]
        }
      },
      recipientRows() {
        const map = new Map()

        const bump = (address, kind, row) => {
          const email = String(address || '').trim()
          if (!email) return

          const key = email.toLowerCase()
          let entry = map.get(key)

          if (!entry) {
            entry = { key, email, to: 0, cc: 0, total: 0, last: null, branchSet: new Set() }
            map.set(key, entry)
          }

          entry[kind] += 1
          entry.total += 1
          if (row.branchLabel) entry.branchSet.add(row.branchLabel)

          const arrived = new Date(row.arrived_date)
          if (!isNaN(arrived.getTime()) && (!entry.last || arrived > entry.last)) entry.last = arrived
        }

        for (const row of this.visibleRows) {
          row.emailed_to.forEach(a => bump(a, 'to', row))
          row.emailed_cc.forEach(a => bump(a, 'cc', row))
        }

        return [...map.values()]
          .map(entry => ({ ...entry, branches: entry.branchSet.size }))
          .sort((a, b) => b.total - a.total || a.email.localeCompare(b.email))
      },
      refusedRows() {
        const map = new Map()

        const bump = (address, reason) => {
          const email = String(address || '').trim()
          if (!email) return

          const key = email.toLowerCase()
          let entry = map.get(key)

          if (!entry) {
            entry = { key, email, total: 0, reasonSet: new Set() }
            map.set(key, entry)
          }

          entry.total += 1
          entry.reasonSet.add(reason)
        }

        for (const row of this.visibleRows) {
          row.refused_not_approver.forEach(a => bump(a, 'not a listed recipient for the loan'))
          row.refused_loan_officer.forEach(a => bump(a, "the loan's own officer"))
        }

        return [...map.values()]
          .map(entry => ({ ...entry, reasons: [...entry.reasonSet] }))
          .sort((a, b) => b.total - a.total || a.email.localeCompare(b.email))
      },
      noRecipientRows() {
        return this.visibleRows.filter(r => r.has_routing && !r.recipientCount)
      },
      branchRows() {
        return this.groupBy(
          this.visibleRows,
          row => row.branchLabel || 'No branch on the item'
        ).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      },
      officerRows() {
        return this.groupBy(
          this.visibleRows,
          row => row.loan_officer_name || 'No loan officer on the item'
        ).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      },
      totalPages() {
        return Math.max(1, Math.ceil(this.visibleRows.length / this.pageSize))
      },
      pagedRows() {
        const start = (this.page - 1) * this.pageSize
        return this.visibleRows.slice(start, start + this.pageSize)
      }
    },
    watch: {
      // A filter change can leave the view past the end of a now-shorter list.
      visibleRows() {
        if (this.page > this.totalPages) this.page = 1
      }
    },
    async mounted() {
      await this.loadAccess()
      if (this.isAuthorized) await this.loadData()
    },
    methods: {
      // One call, before anything else: there is no point fetching alerts this user may not read,
      // and the endpoint would refuse them anyway.
      async loadAccess() {
        this.accessLoading = true
        this.accessError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const response = await api.GetConcessionUserContext(tokenResponse.accessToken)
          this.isConcessionsAdmin = response?.data?.data?.is_concessions_admin === true
        } catch (e) {
          this.isConcessionsAdmin = false
          this.accessError = e?.response?.data?.message || e?.message || 'Could not check your access.'
        } finally {
          this.accessLoading = false
        }
      },
      async loadData() {
        this.loading = true
        this.errorMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const response = await api.GetRevenueAlertAnalysis(this.days, token)
          const payload = response?.data

          if (payload && payload.issuccess === false) {
            throw new Error(payload.message || 'Could not load revenue alerts.')
          }

          const data = payload?.data || payload || []
          this.rows = (Array.isArray(data) ? data : []).map(row => this.decorate(row))
          this.loadedAt = new Date()
          this.page = 1
        } catch (e) {
          this.rows = []
          this.errorMessage = e?.response?.data?.message || e?.message || 'Could not load revenue alerts.'
        } finally {
          this.loading = false
        }
      },
      // Everything the table and the aggregates need, computed once per row rather than per
      // render. Nulls are preserved as null on purpose — a missing branch margin is not a zero,
      // and averaging it as one would understate every figure on the page.
      decorate(raw) {
        const row = { ...raw }

        row.emailed_to = Array.isArray(raw.emailed_to) ? raw.emailed_to : []
        row.emailed_cc = Array.isArray(raw.emailed_cc) ? raw.emailed_cc : []
        row.refused_not_approver = Array.isArray(raw.refused_not_approver) ? raw.refused_not_approver : []
        row.refused_loan_officer = Array.isArray(raw.refused_loan_officer) ? raw.refused_loan_officer : []

        row.has_routing = raw.has_routing === true
        row.has_summary = raw.has_summary === true
        row.note_count = this.num(raw.note_count) || 0
        row.external_note_count = this.num(raw.external_note_count) || 0

        for (const field of [
          'deposit_bps', 'threshold_bps', 'loan_amount', 'forecast_deposit', 'branch_margin',
          'branch_concession', 'corp_concession', 'lender_credit', 'lo_comp'
        ]) {
          row[field] = this.num(raw[field])
        }

        row.recipientCount = row.emailed_to.length + row.emailed_cc.length
        row.branchLabel = raw.branch || raw.orgid || ''

        row.searchText = [
          raw.loan_number,
          raw.borrower_first,
          raw.borrower_last,
          raw.loan_officer_name,
          row.branchLabel,
          raw.channel,
          raw.loan_product,
          raw.lead_type,
          raw.request_description,
          ...row.emailed_to,
          ...row.emailed_cc,
          ...row.refused_not_approver,
          ...row.refused_loan_officer
        ].filter(Boolean).join(' ').toLowerCase()

        return row
      },
      // ── Small numeric helpers ────────────────────────────────────────────────
      // Absent (the API omits nulls) and unparseable both read as null, never as 0.
      num(value) {
        if (value === null || value === undefined || value === '') return null
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
      },
      sum(values) {
        return (values || []).reduce((total, v) => total + (this.num(v) || 0), 0)
      },
      median(values) {
        const list = (values || []).filter(x => typeof x === 'number').sort((a, b) => a - b)
        if (!list.length) return null

        const mid = Math.floor(list.length / 2)
        return list.length % 2 ? list[mid] : Math.round((list[mid - 1] + list[mid]) / 2)
      },
      ageSeconds(value) {
        const date = new Date(value)
        if (isNaN(date.getTime())) return null
        return Math.max(0, Math.round((Date.now() - date.getTime()) / 1000))
      },
      startOfDay(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
      },
      // Monday, so a week's bar covers a working week rather than splitting it.
      startOfWeek(date) {
        const start = this.startOfDay(date)
        const shift = (start.getDay() + 6) % 7
        start.setDate(start.getDate() - shift)
        return start
      },
      groupBy(rows, keyOf) {
        const groups = new Map()

        for (const row of rows) {
          const label = keyOf(row)
          if (!groups.has(label)) {
            groups.set(label, {
              key: label,
              label,
              count: 0,
              bps: [],
              branchMargin: 0,
              concessions: 0,
              loComp: 0,
              loanSet: new Set()
            })
          }

          const group = groups.get(label)
          group.count += 1
          if (row.deposit_bps !== null) group.bps.push(row.deposit_bps)
          group.branchMargin += row.branch_margin || 0
          group.concessions += (row.branch_concession || 0) + (row.corp_concession || 0)
          group.loComp += row.lo_comp || 0
          if (row.loan_number || row.loan_id) group.loanSet.add(row.loan_number || row.loan_id)
        }

        return [...groups.values()].map(group => ({
          ...group,
          loans: group.loanSet.size,
          avgBps: group.bps.length ? this.sum(group.bps) / group.bps.length : null
        }))
      },
      // ── Sorting ─────────────────────────────────────────────────────────────
      sort(field) {
        if (this.sortBy === field) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        } else {
          this.sortBy = field
          this.sortDir = field === 'arrived_date' ? 'desc' : 'asc'
        }
        this.page = 1
      },
      sortMark(field) {
        if (this.sortBy !== field) return ''
        return this.sortDir === 'asc' ? '▲' : '▼'
      },
      compareRows(a, b) {
        const field = this.sortBy
        let aVal = a[field]
        let bVal = b[field]

        if (field === 'arrived_date') {
          aVal = new Date(a.arrived_date).getTime()
          bVal = new Date(b.arrived_date).getTime()
        }

        if (field === 'request_type') {
          aVal = this.typeLabel(a.request_type)
          bVal = this.typeLabel(b.request_type)
        }

        if (aVal === bVal) return 0
        // Unknowns sort last in both directions — they are absence, not a low value.
        if (aVal === null || aVal === undefined || aVal === '') return 1
        if (bVal === null || bVal === undefined || bVal === '') return -1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
        }

        const result = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true, sensitivity: 'base'
        })
        return this.sortDir === 'asc' ? result : -result
      },
      toggleExpand(row) {
        this.expandedId = this.expandedId === row.id ? '' : row.id
      },
      // ── Labels and formatting ───────────────────────────────────────────────
      typeKey(requestType) {
        return String(requestType || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
      },
      typeLabel(requestType) {
        const key = this.typeKey(requestType)
        if (TYPE_META[key]) return TYPE_META[key].label

        // An unknown type is shown as it arrived, title cased, rather than hidden.
        return key
          ? key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          : 'Unknown'
      },
      typePillClass(requestType) {
        const key = this.typeKey(requestType)
        return TYPE_META[key] ? TYPE_META[key].pill : 'type-other'
      },
      statusKey(status) {
        return String(status || 'not_started').trim().toLowerCase()
      },
      statusLabel(status) {
        const key = this.statusKey(status)
        if (key === 'in_progress') return 'In Progress'
        if (key === 'done' || key === 'completed') return 'Done'
        if (key === 'cancelled') return 'Cancelled'
        return 'Not Started'
      },
      statusClass(status) {
        const key = this.statusKey(status)
        if (key === 'in_progress') return 'status-progress'
        if (key === 'done' || key === 'completed') return 'status-done'
        if (key === 'cancelled') return 'status-cancelled'
        return 'status-new'
      },
      borrowerName(row) {
        return [row.borrower_first, row.borrower_last].filter(Boolean).join(' ') || '—'
      },
      allRecipients(row) {
        return [...row.emailed_to, ...row.emailed_cc]
      },
      splitAddresses(value) {
        return String(value || '')
          .split(/[,;]/)
          .map(x => x.trim())
          .filter(Boolean)
      },
      formatBps(value) {
        const parsed = this.num(value)
        if (parsed === null) return '—'
        return `${parsed.toFixed(2)} bps`
      },
      formatMoney(value) {
        const parsed = this.num(value)
        if (parsed === null) return '—'
        return parsed.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0
        })
      },
      // Compact form for the aggregate cards, where a full figure would wrap.
      formatMoneyShort(value) {
        const parsed = this.num(value)
        if (parsed === null) return '—'

        const sign = parsed < 0 ? '-' : ''
        const abs = Math.abs(parsed)

        if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`
        if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}k`
        return `${sign}$${Math.round(abs)}`
      },
      // Impossible negatives are masked rather than printed: they come from timestamp maths in
      // the producer, not from anything the desk did, and a negative turnaround on screen reads
      // as a bug in this page. Same treatment the lock desk queue gives them.
      formatDuration(seconds) {
        const parsed = this.num(seconds)
        if (parsed === null || parsed < 0) return '—'

        if (parsed < 60) return `${Math.round(parsed)}s`

        const minutes = Math.floor(parsed / 60)
        if (minutes < 60) return `${minutes}m`

        const hours = Math.floor(minutes / 60)
        const restMinutes = minutes % 60
        if (hours < 24) return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`

        const daysPart = Math.floor(hours / 24)
        const restHours = hours % 24
        return restHours ? `${daysPart}d ${restHours}h` : `${daysPart}d`
      },
      formatDate(value) {
        if (!value) return '—'
        const date = new Date(value)
        if (isNaN(date.getTime())) return '—'
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
      },
      formatDateTime(value) {
        if (!value) return '—'
        const date = new Date(value)
        if (isNaN(date.getTime())) return '—'
        return date.toLocaleString('en-US', {
          month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit'
        })
      },
      percentText(part, whole) {
        if (!whole) return '0%'
        return `${Math.round((part / whole) * 100)}%`
      },
      // ── Loan links ──────────────────────────────────────────────────────────
      pollyUrl(row) {
        if (!row?.loan_id) return null
        return `https://lx.pollyex.com/pe/loans/${row.loan_id}?mode=lock&orgId=${this.pollyOrgId}`
      },
      encompassUrl(row) {
        if (!row?.loan_id) return null
        return `https://www.encompassloconnect.com/pipeline/${row.loan_id}`
      },
      openLoanLink(url, event) {
        if (openLoanInWindow(url, event)) event.preventDefault()
      },
      // ── Export ──────────────────────────────────────────────────────────────
      // The whole filtered set, one row per alert, recipients included — the page's own tables
      // aggregate, and the reason to export is usually to aggregate differently.
      exportCsv() {
        const headers = [
          'Arrived (UTC)', 'Type', 'Trigger', 'Loan Number', 'Borrower', 'Loan Officer', 'Branch',
          'Channel', 'Deposit bps', 'Threshold bps', 'Loan Product', 'Loan Amount',
          'Forecast Deposit', 'Branch Margin', 'Branch Concession', 'Corp Concession',
          'Lender Credit', 'LO Comp', 'Lead Type', 'Emailed To', 'CC', 'Delivered To (redirect)',
          'Refused (not a listed recipient)', 'Refused (own loan officer)', 'CC Setting',
          'Routing Recorded', 'Status', 'Picked Up (UTC)', 'Completed (UTC)', 'Worked By',
          'Wait (seconds)', 'Turnaround (seconds)', 'Notes'
        ]

        const cell = value => {
          const text = value === null || value === undefined ? '' : String(value)
          return `"${text.replace(/"/g, '""')}"`
        }

        const lines = [headers.map(cell).join(',')]

        for (const row of this.visibleRows) {
          lines.push([
            row.arrived_date,
            this.typeLabel(row.request_type),
            row.request_description,
            row.loan_number,
            this.borrowerName(row) === '—' ? '' : this.borrowerName(row),
            row.loan_officer_name,
            row.branchLabel,
            row.channel,
            row.deposit_bps,
            row.threshold_bps,
            row.loan_product,
            row.loan_amount,
            row.forecast_deposit,
            row.branch_margin,
            row.branch_concession,
            row.corp_concession,
            row.lender_credit,
            row.lo_comp,
            row.lead_type,
            row.emailed_to.join('; '),
            row.emailed_cc.join('; '),
            row.redirected_to,
            row.refused_not_approver.join('; '),
            row.refused_loan_officer.join('; '),
            row.configured_ccs,
            row.has_routing ? 'yes' : 'no',
            this.statusLabel(row.status),
            row.assigned_utc,
            row.completed_utc,
            row.completed_by_name || row.started_by_name || row.assigned_to_name,
            row.wait_seconds,
            row.duration_seconds,
            row.note_count
          ].map(cell).join(','))
        }

        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.download = `revenue-analysis-${this.days}d.csv`
        link.click()

        URL.revokeObjectURL(url)
      }
    }
  }
</script>

<style scoped>
  /* Sized as the leftover of #app's flex column, the same way the other lock desk dashboards
     are: a hardcoded viewport subtraction overshoots, and scrolling belongs to the page rather
     than to a scrollbar around the whole app. */
  .revenue-analysis-page {
    padding: 18px;
    background: #f7f8fb;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: #1f2a44;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex: 0 0 auto;
  }

  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .page-icon {
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex: 0 0 auto;
  }

  .page-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    font-weight: 500;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .page-error {
    padding: 10px 12px;
    border-radius: 5px;
    background: #fff5f5;
    border: 1px solid #f3d4d8;
    color: #a4303c;
    font-size: 13px;
    font-weight: 600;
  }

  .redirect-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 5px;
    background: #fff7ed;
    border: 1px solid #f3d9b0;
    color: #8a5314;
    font-size: 13px;
    line-height: 1.5;
  }

  .redirect-banner i {
    font-size: 15px;
    margin-top: 2px;
  }

  /* ── Overview cards ── */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    flex: 0 0 auto;
  }

  .stat-card {
    background: #fdfdfe;
    border: 1px solid #e8ebf2;
    border-radius: 5px;
    padding: 12px;
  }

  .stat-card.stat-warn {
    border-color: #f2d7d9;
    background: #fffafb;
  }

  .stat-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .stat-icon {
    font-size: 13px;
    color: #6a7b9f;
  }

  .stat-label {
    font-size: 12px;
    color: #7b8397;
    font-weight: 600;
  }

  .stat-value {
    font-size: 22px;
    font-weight: 800;
    color: #1f2a44;
    line-height: 1.1;
  }

  .stat-foot {
    margin-top: 6px;
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 500;
  }

  /* ── Panels ── */
  .panel-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    flex: 0 0 auto;
  }

  .panel {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    padding: 12px;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    gap: 10px;
  }

  .panel-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .panel-icon {
    color: #5f79c7;
    font-size: 14px;
  }

  .panel-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .panel-note {
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 500;
  }

  .panel-count {
    font-size: 13px;
    font-weight: 700;
    color: #7b8397;
  }

  .panel-empty {
    padding: 18px 4px;
    color: #8a93a8;
    font-size: 13px;
  }

  .sub-title {
    font-size: 12px;
    font-weight: 700;
    color: #5c6780;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 8px;
  }

  /* ── Mix bars ── */
  .mix-row {
    padding: 8px 0;
    border-bottom: 1px solid #f0f3f8;
  }

  .mix-row:last-child {
    border-bottom: none;
  }

  /* A type that fired nothing is listed but recedes — it is context, not a finding. */
  .mix-row-zero {
    opacity: 0.55;
  }

  .mix-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .mix-count {
    font-size: 16px;
    font-weight: 800;
    color: #1f2a44;
  }

  .mix-bar {
    height: 8px;
    border-radius: 999px;
    background: #eef1f7;
    overflow: hidden;
  }

  .mix-bar-fill {
    height: 100%;
    border-radius: 999px;
    background: #6a7b9f;
  }

  .mix-bar-fill.type-deposit { background: #c05650; }
  .mix-bar-fill.type-wholesale { background: #c08a2e; }
  .mix-bar-fill.type-other { background: #8a93a8; }

  .mix-foot {
    margin-top: 6px;
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 500;
  }

  /* ── Trend ── */
  .trend-chart {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    height: 150px;
    overflow-x: auto;
  }

  .trend-col {
    flex: 1 1 0;
    min-width: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
  }

  .trend-bar-wrap {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: flex-end;
  }

  .trend-bar {
    width: 100%;
    min-height: 2px;
    border-radius: 3px 3px 0 0;
    background: #7b93cc;
  }

  .trend-count {
    font-size: 10.5px;
    font-weight: 700;
    color: #5c6780;
    height: 14px;
  }

  .trend-label {
    font-size: 10px;
    color: #98a1b5;
    white-space: nowrap;
  }

  /* ── Routing ── */
  .panel-hint {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 10px 12px;
    margin-bottom: 12px;
    border-radius: 5px;
    background: #f5f8fd;
    border: 1px solid #dde6f4;
    color: #43567d;
    font-size: 12.5px;
    line-height: 1.5;
  }

  .panel-hint i {
    color: #5f79c7;
    margin-top: 2px;
  }

  .routing-stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .routing-stat {
    background: #fdfdfe;
    border: 1px solid #e8ebf2;
    border-radius: 5px;
    padding: 10px 12px;
  }

  .routing-stat-warn {
    border-color: #f2d7d9;
    background: #fffafb;
  }

  .routing-stat-value {
    font-size: 18px;
    font-weight: 800;
    color: #1f2a44;
  }

  .routing-stat-label {
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 600;
  }

  .routing-tables {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .routing-table-wrap {
    min-width: 0;
  }

  .nobody-block {
    margin-top: 14px;
  }

  .nobody-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .nobody-list li {
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 5px 0;
    border-bottom: 1px solid #f0f3f8;
    font-size: 12.5px;
  }

  .nobody-meta {
    color: #8a93a8;
    font-size: 11.5px;
  }

  .nobody-more {
    margin-top: 6px;
    font-size: 11.5px;
    color: #8a93a8;
  }

  /* ── Tables ── */
  /* Natural height. This is a report read top to bottom, so the PAGE is the only vertical
     scroller and the tables inside it cap themselves (see .table-wrap). */
  .table-panel {
    flex: 0 0 auto;
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) 170px 170px 190px 210px;
    gap: 8px;
    margin-bottom: 10px;
  }

  .search-input,
  .filter-select {
    height: 38px;
    border: 1px solid #dfe4ee;
    background: #fcfcfe;
    border-radius: 5px;
    padding: 0 14px;
    font-size: 13px;
    width: 100%;
    color: #23314f;
    outline: none;
  }

  .search-input::placeholder {
    color: #94a0b8;
  }

  .search-input:focus,
  .filter-select:focus {
    border-color: #cfd8e8;
    box-shadow: 0 0 0 2px rgba(49, 93, 181, 0.08);
  }

  .days-select {
    min-width: 150px;
  }

  .refresh-btn,
  .export-btn,
  .page-btn,
  .polly-btn,
  .encompass-btn {
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #22314f;
    height: 38px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
  }

  .refresh-btn:hover,
  .export-btn:hover,
  .page-btn:hover,
  .polly-btn:hover,
  .encompass-btn:hover {
    background: #f4f7fc;
    border-color: #cfd8e8;
  }

  .export-btn {
    color: #315db5;
    background: #f5f8fc;
    border-color: #d9e2f0;
  }

  .refresh-btn:disabled,
  .export-btn:disabled,
  .page-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .polly-btn,
  .encompass-btn {
    height: 30px;
    padding: 0 12px;
    font-size: 12px;
  }

  .polly-btn {
    color: #0f766e;
    background: #f3fbf9;
    border-color: #cfe9e2;
  }

  .encompass-btn {
    color: #315db5;
    background: #f5f8fc;
    border-color: #d9e2f0;
  }

  .polly-btn.is-disabled,
  .encompass-btn.is-disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  /* A DEFINITE height, not flex: 1. A sticky header sticks to its nearest scrolling ancestor,
     so a table whose own wrapper never becomes a scroll port hands its header to the PAGE — and
     the header then floats at the top of the viewport, under the app nav, while its own rows
     scroll past it. Capping the height here is what makes this wrapper the scroll port, which is
     the only place a sticky header behaves. Every sticky header on this page is inside one of
     these two boxes; nothing sticks to the page itself. */
  .table-wrap,
  .scroll-box {
    overflow: auto;
    border: 1px solid #edf0f5;
    border-radius: 5px;
    background: #fdfdfe;
  }

  .table-wrap {
    max-height: 62vh;
  }

  /* The recipient and refused lists are unbounded — one row per address seen in the window — so
     they scroll inside the panel rather than stretching it down the page. */
  .scroll-box {
    max-height: 330px;
  }

  .table-wrap th,
  .scroll-box th {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  .alerts-table {
    min-width: 1500px;
  }

  .data-table th,
  .data-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #edf0f5;
    font-size: 12.5px;
    text-align: left;
    vertical-align: middle;
  }

  .data-table th {
    background: #f7f9fc;
    color: #6f7b92;
    font-weight: 700;
    white-space: nowrap;
  }

  .alerts-table th {
    cursor: pointer;
  }

  .data-table td.num,
  .data-table th.num {
    text-align: right;
  }

  .data-table .strong {
    font-weight: 700;
    color: #24314f;
  }

  .data-table .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
  }

  .data-table td.negative {
    color: #a4302a;
    font-weight: 700;
  }

  .empty-cell,
  .table-state {
    padding: 22px;
    text-align: center;
    color: #8a93a8;
    font-size: 13px;
  }

  .reason-cell {
    color: #7b8397;
    font-size: 11.5px;
  }

  .alert-row {
    cursor: pointer;
  }

  .alert-row:hover {
    background: #f8faff;
  }

  .alert-row.is-open {
    background: #f4f7fd;
  }

  .expand-col {
    width: 28px;
    color: #98a1b5;
  }

  /* ── Pills and chips ── */
  .type-pill,
  .status-pill,
  .routing-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 999px;
    padding: 3px 9px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    border: 1px solid transparent;
  }

  .type-pill.type-deposit {
    background: #fdecec;
    color: #a4302a;
    border-color: #f4c4c1;
  }

  .type-pill.type-wholesale {
    background: #fff7e8;
    color: #8a5a14;
    border-color: #f3dfb7;
  }

  .type-pill.type-other {
    background: #f1f2f5;
    color: #6d7789;
    border-color: #e4e9f2;
  }

  .status-pill.status-new {
    background: #f2f4f7;
    color: #6d7789;
    border-color: #e1e5ec;
  }

  .status-pill.status-progress {
    background: #edf4ff;
    color: #3d6fd1;
    border-color: #d7e5fd;
  }

  .status-pill.status-done {
    background: #eef9f0;
    color: #2c7a4b;
    border-color: #d9eedf;
  }

  .status-pill.status-cancelled {
    background: #f6f2f2;
    color: #8a6d6d;
    border-color: #e6dcdc;
  }

  .routing-chip {
    background: #f4f7fc;
    color: #395583;
    border-color: #dde5f2;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
  }

  .routing-chip.chip-warn {
    background: #fff5f5;
    color: #a4303c;
    border-color: #f3d4d8;
  }

  .routing-chip.chip-muted {
    background: #f5f6f8;
    color: #8a93a8;
    border-color: #e6e9ef;
  }

  .routing-more {
    color: #7b8397;
    font-weight: 700;
  }

  /* ── Expanded detail ── */
  .detail-row td {
    background: #f9fbff;
    padding: 14px 16px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }

  .detail-block {
    min-width: 0;
  }

  .detail-empty {
    font-size: 12.5px;
    color: #8a93a8;
    line-height: 1.5;
  }

  .detail-list {
    margin: 0;
  }

  .detail-list > div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #edf1f8;
  }

  .detail-list dt {
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 600;
    flex: 0 0 auto;
  }

  .detail-list dd {
    margin: 0;
    font-size: 12.5px;
    color: #24314f;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }

  .routing-line {
    display: flex;
    gap: 10px;
    padding: 5px 0;
    border-bottom: 1px solid #edf1f8;
  }

  .routing-label {
    flex: 0 0 74px;
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding-top: 3px;
  }

  .routing-addresses {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    align-items: center;
    min-width: 0;
  }

  .address-chip {
    display: inline-flex;
    border-radius: 5px;
    padding: 2px 7px;
    font-size: 11.5px;
    font-weight: 600;
    background: #eef4ff;
    color: #2f5798;
    border: 1px solid #d7e5fd;
    word-break: break-all;
  }

  .address-chip.chip-refused {
    background: #fff5f5;
    color: #a4303c;
    border-color: #f3d4d8;
  }

  .address-chip.chip-redirect {
    background: #fff7ed;
    color: #8a5314;
    border-color: #f3d9b0;
  }

  .routing-none {
    font-size: 12px;
    color: #a4303c;
    font-weight: 600;
    padding-top: 3px;
  }

  .routing-note-text {
    font-size: 11px;
    color: #8a93a8;
    font-weight: 500;
  }

  .detail-links {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }

  .pagination-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 4px 0;
    gap: 12px;
    flex: 0 0 auto;
  }

  .pagination-info {
    color: #7b8397;
    font-size: 12.5px;
    font-weight: 500;
  }

  .pagination-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .page-number {
    font-size: 13px;
    color: #5c6780;
    font-weight: 600;
  }

  .state-card {
    margin: auto;
    padding: 28px 36px;
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 12px;
    color: #7b8397;
    font-size: 13px;
  }

  /* ── Not authorized ── */
  .not-authorized-card {
    margin: auto;
    text-align: center;
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 12px;
    padding: 40px 48px;
  }

  .not-authorized-icon {
    font-size: 28px;
    color: #98a1b5;
    margin-bottom: 10px;
  }

  .not-authorized-title {
    font-size: 17px;
    font-weight: 700;
    color: #1f2a44;
  }

  .not-authorized-text {
    margin-top: 6px;
    font-size: 13px;
    color: #7b8397;
  }

  @media (max-width: 1500px) {
    .stat-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .routing-stats {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 1100px) {
    .panel-row,
    .routing-tables,
    .detail-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .toolbar {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
  }
</style>
